'use client'

import React, { forwardRef, HTMLAttributes, ReactElement, Ref, useState, useImperativeHandle } from 'react'
import { useTableContext } from '../../../../providers'
import { CollapseRowCell } from './CollapseRowCell'
import { CollapseContentCell } from './CollapseContentCell'
import { TableCellProps } from './TableCell'
import { TagColors, colorBackgroundClasses } from '../../../atoms'

export interface TableRowProps extends Omit<HTMLAttributes<HTMLTableRowElement>, 'color'> {
    children?: React.ReactNode
    collapsedContent?: React.ReactNode
    onClick?: () => void
    color?: TagColors
    className?: string
}

export interface TableRowRef {
    toggleIsCollapsed: () => void
    isCollapsed: boolean
}

export const TableRow = forwardRef<TableRowRef, TableRowProps>(
    ({ children, collapsedContent, color, onClick, ...props }, ref) => {
        const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
        const { isCollapsible, isInline, isNested, hasBorder, columns } = useTableContext()

        const toggleIsCollapsed = () => {
            setIsCollapsed((collapsed) => !collapsed)
        }

        useImperativeHandle(
            ref,
            () => ({
                toggleIsCollapsed,
                isCollapsed,
            }),
            [isCollapsed],
        )

        const childrenWithColumnProp = React.Children.map<React.ReactNode, React.ReactNode>(
            React.Children.toArray(children).filter((child) => !!child), // This filters out possible conditional table-cells. These conditional cells would otherwise be a child with value "false" and therefore mess up the indexing of the columns
            (element, index) => {
                if (React.isValidElement(element)) {
                    return React.cloneElement(element as ReactElement<TableCellProps>, {
                        column: columns[index],
                    })
                }
                return element
            },
        )

        const getClasses = (): string => {
            let classes = 'border-border-default relative'
            if (isCollapsed) classes += ' border-b'
            if (isInline && hasBorder) classes += ' border-x'
            if (isNested || !hasBorder) classes += ' last:border-b-0'
            if (color !== null && color !== undefined) classes += ` ${colorBackgroundClasses[color]}`
            if (!!props.className) classes += ` ${props.className}`
            return classes
        }

        return (
            <>
                <tr
                    {...props}
                    className={getClasses()}
                    onClick={() => {
                        onClick && onClick()
                    }}
                >
                    {childrenWithColumnProp}
                    {isCollapsible && (
                        <CollapseRowCell
                            isCollapsed={isCollapsed}
                            toggleIsCollapsed={toggleIsCollapsed}
                            hasContent={!!collapsedContent}
                        />
                    )}
                </tr>
                {collapsedContent && (
                    <CollapseContentCell isCollapsed={isCollapsed}>{collapsedContent}</CollapseContentCell>
                )}
            </>
        )
    },
)
